import { Alert, Box, Card, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material'
import PlotCellImage from 'components/cell-visualizations/PlotCellImage'
import useSessionApi from 'components/cell-visualizations/useSessionApi'
import { checkForOverlappingMorphotypes } from 'components/cell-visualizations/utils'
import { DeepcellDialog, DeepcellTextField } from 'components/shared'
import useFlags from 'components/shared/useFlags'
import moment from 'moment'
import { useMemo, useState } from 'react'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import { useXGBoostAccess } from 'utils/useXGBoostAccess'
import {
  ClassifierSuccessOutput,
  TrainClassifierSuccessDialog,
} from './TrainClassifierSuccessDialog'

export interface TrainClassifierDialogProps {
  open?: boolean
  onTrainClassifier?(): void
  onClose?(): void
}
export const TrainClassifierDialog = (props: TrainClassifierDialogProps): JSX.Element => {
  const { open, onTrainClassifier, onClose } = props

  const { saveVersionConfig } = useSessionApi()

  const { cellVisualizationsModelNameInvalidCharacters } = useFlags()
  const invalidModelNameChars = cellVisualizationsModelNameInvalidCharacters.split('')
  const hasXGBoostAccess = useXGBoostAccess()

  const {
    getMergedPinnedMorphotypes,
    cellVisualizations: { projectCode, sessionData },
  } = useCellVisualizationsSlice()
  const name = sessionData?.name

  // Check for overlapping morphotypes
  // Memoize based on the actual data that affects morphotype definitions
  const overlappingPairs = useMemo(() => {
    const pinnedMorphotypes = getMergedPinnedMorphotypes()
    if (!pinnedMorphotypes) return []
    return checkForOverlappingMorphotypes(pinnedMorphotypes)
  }, [getMergedPinnedMorphotypes])

  const [classifierName, setClassifierName] = useState('')
  const [error, setError] = useState('')
  const [successOutput, setSuccessOutput] = useState<ClassifierSuccessOutput>()
  const [showModal, setShowModal] = useState<boolean>(false)
  const [excludedMorphotypeIds, setExcludedMorphotypeIds] = useState<Set<number>>(new Set())
  const [isTraining, setIsTraining] = useState(false)

  const handleClose = () => {
    setError('')
    setClassifierName('')
    setExcludedMorphotypeIds(new Set())
    setIsTraining(false)
    if (onClose) onClose()
  }

  // Get morphotypes that will be included in training (active and not excluded)
  const morphotypesForTraining = useMemo(() => {
    const pinnedMorphotypes = getMergedPinnedMorphotypes()
    if (!pinnedMorphotypes) return []
    return pinnedMorphotypes.filter((m) => m.active && !excludedMorphotypeIds.has(m.id))
  }, [getMergedPinnedMorphotypes, excludedMorphotypeIds])

  // Check for overlaps only among morphotypes that will be included in training
  const trainingOverlappingPairs = useMemo(() => {
    if (!morphotypesForTraining) return []
    return checkForOverlappingMorphotypes(morphotypesForTraining)
  }, [morphotypesForTraining])

  // Check if any cluster-based morphotypes are selected for training
  const hasClusterBasedMorphotypes = useMemo(() => {
    return morphotypesForTraining.some((m) => m.isClusterBased === true)
  }, [morphotypesForTraining])

  // Disable training if cluster-based morphotypes are selected without XGBoost access
  const requiresXGBoost = hasClusterBasedMorphotypes && !hasXGBoostAccess

  const handleMorphotypeToggle = (morphotypeId: number, checked: boolean) => {
    const newExcludedIds = new Set(excludedMorphotypeIds)
    if (checked) {
      newExcludedIds.delete(morphotypeId)
    } else {
      newExcludedIds.add(morphotypeId)
    }
    setExcludedMorphotypeIds(newExcludedIds)
  }

  const handleTrainClassifierClick = async () => {
    if (!classifierName) {
      setError('Classifier Name is required')
      return
    }

    if (trainingOverlappingPairs.length > 0) {
      setError(
        'Cannot train classifier with overlapping morphotypes. Please resolve overlaps first.'
      )
      return
    }

    // Prevent multiple submissions
    if (isTraining) {
      return
    }

    setIsTraining(true)

    // Convert excluded morphotype IDs to their names for the API
    const pinnedMorphotypes = getMergedPinnedMorphotypes()
    const excludedMorphotypeNames = pinnedMorphotypes
      ?.filter((m) => excludedMorphotypeIds.has(m.id))
      ?.map((m) => m.name) || []

    const errorOutput = await saveVersionConfig({
      classifierName,
      projectCode,
      trainClassifier: true,
      excludeMorphotypesFromTraining: excludedMorphotypeNames,
      useXGBoost: hasXGBoostAccess,
    })

    setIsTraining(false)

    if (errorOutput) {
      // Keep dialog open so user can retry - toast notification is shown by useSessionApi
      return
    }

    // Success path
    setShowModal(true)
    setSuccessOutput({
      classifierName,
      sessionName: name,
      trainedDate: moment().format('MMM Do YYYY, h:mm:ss a'),
    })
    if (onTrainClassifier) onTrainClassifier()
    if (onClose) onClose()
  }

  /** Flag invalid characters that don't work elsewhere in the Deepcell platform
   *
   * For example, Model Management Service doesn't handle ':' well, since we use that in canonical model identifiers
   * See ModelVer, for example
   */
  function getInvalidChars(modelName: string) {
    return invalidModelNameChars.filter((char) => modelName.includes(char))
  }

  /** Trim whitespace from beginning and end.  This breaks Controller software (CBS-715) */
  function sanitizeModelName() {
    setClassifierName(classifierName.trim())
  }

  const onClassifierChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const invalidChars = getInvalidChars(e.target.value)

    if (invalidChars.length > 0) {
      const suffix = invalidChars.length > 1 ? 's' : ''
      setError(`Classifier name cannot contain the character${suffix} ${invalidChars.join('')}`)
    } else {
      setError('')
    }
    setClassifierName(e.target.value)
  }

  return (
    <>
      <DeepcellDialog
        handleCancel={handleClose}
        handleConfirm={handleTrainClassifierClick}
        open={!!open}
        okLabel="Train Classifier"
        okDisabled={!!error || trainingOverlappingPairs.length > 0 || requiresXGBoost || isTraining}
        loadingButton
        loadingButtonState={isTraining}
        titleLabel="Train Morphotype Classifier"
      >
        <Stack spacing={2} sx={{ width: 400, px: 1 }}>
          <Typography align="center">
            Please confirm you would like to train a classifier to sort cells on instrument using
            morphotypes
          </Typography>

          {trainingOverlappingPairs.length > 0 && (
            <Alert severity="warning" icon={false} sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                The following morphotypes have overlapping cells:
              </Typography>
              {trainingOverlappingPairs.map((pair) => (
                <Typography
                  key={`${pair.morphotype1.id}-${pair.morphotype2.id}`}
                  variant="body2"
                  sx={{ ml: 1 }}
                >
                  {`• ${pair.morphotype1.name} overlaps with ${pair.morphotype2.name}`}
                </Typography>
              ))}
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                Uncheck one of the overlapping morphotypes below to exclude it from training.
              </Typography>
            </Alert>
          )}
          {requiresXGBoost && (
            <Alert severity="warning" icon={false}>
              <Typography variant="body2">
              Training a classifier with a cluster-based morphotype requires an upcoming release of the Controller software.
              Please exclude cluster-based morphotypes from training.
              </Typography>
            </Alert>
          )}
          <DeepcellTextField
            label="Classifier Name"
            value={classifierName}
            onChange={onClassifierChange}
            onBlur={sanitizeModelName}
            error={!!error}
            helperText={error}
            autoFocus
          />
          <Card sx={{ p: 2 }}>
            <Typography mb={1}>Morphotypes for Training</Typography>
            <Stack spacing={1} maxHeight={300} sx={{ overflowY: 'auto' }}>
              {getMergedPinnedMorphotypes()?.map((morphotype) => {
                if (!morphotype.active) return null

                const isExcluded = excludedMorphotypeIds.has(morphotype.id)
                const isOverlapping = overlappingPairs.some(
                  (pair) =>
                    pair.morphotype1.id === morphotype.id || pair.morphotype2.id === morphotype.id
                )

                return (
                  <Stack
                    key={morphotype.id}
                    direction="row"
                    p={1}
                    spacing={2}
                    borderRadius={1}
                    alignItems="center"
                    sx={{
                      bgcolor: (t) => t.palette.info.light,
                      opacity: isExcluded ? 0.5 : 1,
                      border: isOverlapping ? '2px solid #ff9800' : 'none',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!isExcluded}
                          onChange={(e) => handleMorphotypeToggle(morphotype.id, e.target.checked)}
                          color="primary"
                        />
                      }
                      label=""
                      sx={{ m: 0 }}
                    />
                    <PlotCellImage
                      cellId={morphotype.cells.points?.[0]?.cellId ?? ''}
                      imgProps={{ width: 40, height: 40, style: { borderRadius: 8 } }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography>{morphotype.name}</Typography>
                      {isOverlapping && (
                        <Typography variant="caption" color="warning.main">
                          Overlapping
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                )
              })}
            </Stack>
          </Card>
          <Typography align="center" fontSize="12px" color={(t) => t.palette.text.secondary}>
            Note: Once you publish classifiers it creates a version.You can access versions from top
            navigation.
          </Typography>
        </Stack>
      </DeepcellDialog>
      <TrainClassifierSuccessDialog
        open={showModal}
        onClose={() => {
          setShowModal(false)
          setSuccessOutput(undefined)
        }}
        output={successOutput}
      />
    </>
  )
}

export default TrainClassifierDialog
